import React, {useCallback, useEffect, useState} from "react";
import {MobileView, StyledContent, StyledLayout, WebView} from "./styles";
import {CollapseSection, ItemColumn, ItemLabel, ItemList} from "./FeaturesConst";
import {permissions} from "../../utils/constants/permissions";
import {DropResult} from "react-beautiful-dnd";
import {Form} from "antd";
import CreateEditCategoryModal from "./featuresModel";
import CreateEditQuestionModal from "./PricingPlanModel";
import {checkHasPermission} from "../../utils/helpers/checkHasPermission";
import ConfirmationModal from "../../components/ConfirmationModal";
import {Features, PricingPlan} from "../../types";
import {useAppDispatch, useAppSelector} from "../../utils/hooks/redux";
import {selectUserRole} from "../../redux/features/auth/selectors";
import { selectEstablishmentTypeQuestion, setQuestionData, setSelectedEstablishmentType as setSelectedEstablishmentTypeInQuestion } from "../../redux/features/establishmentTypeQuestion/establishmentTypeQuestionSlice";
import {
    deletePricingPlan,
    fetchPricingPlan,
    addPricingPlan,
    updatePricingPlan
} from "../../redux/features/pricingPlan/pricingPlanAsyncThunk";
import {
    deleteFeatures,
    updateFeatures,
    addFeatures,
    fetchFeatures,
    updateFeaturesOrder
} from "../../redux/features/feature/featuresAsyncThunk";
import {selectFeatures} from "../../redux/features/feature/featuresTypeSlice";
import {selectPricingPlan} from "../../redux/features/pricingPlan/pricingPlanTypeSlice";

const EstablishmentTypeAreYou: React.FC = () => {

    const dispatch = useAppDispatch();
    const { data: features, status: featuresStatus } = useAppSelector(selectFeatures);
    const { data: pricingPlan, status: pricingPlanStatus } = useAppSelector(selectPricingPlan);
    const userRole = useAppSelector(selectUserRole);
    const [form] = Form.useForm();
    const [pricingPlanForm] = Form.useForm();
    const [selectedFeatures, setSelectedFeatures] = useState<Features | null>(null);
    const [featuresList, setFeaturesList] = useState<Features[]>([]);
    const [editingRecord, setEditingRecord] = useState<Features | PricingPlan | null>(null);
    const [showDeleteConfirmItem, setShowDeleteConfirmItem] = useState<{ item: Features | PricingPlan; type: "features" | "pricingPlan" } | null>(null);
    const [modalType, setModalType] = useState<"features" | "pricingPlan" | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [isEstablishmentTypeOpen, setIsEstablishmentTypeOpen] = useState(false);
    const [isEstablishmentTypeQuestionsOpen, setIsEstablishmentTypeQuestionOpen] = useState(false);

    const sanitizeTranslations = (input: any) => {
        if (!input || typeof input !== "object") return undefined;
        const entries = Object.entries(input).filter(([, value]) => value && `${value}`.trim() !== "");
        return entries.length ? Object.fromEntries(entries) : undefined;
    };

    useEffect(() => {
        if (featuresStatus === "idle") {
            dispatch(fetchFeatures());
        }
    }, [dispatch, featuresStatus]);

    useEffect(() => {
        if (pricingPlanStatus === "idle") {
            dispatch(fetchPricingPlan());
        }
    }, [dispatch, pricingPlanStatus]);

    useEffect(() => {
        const establishmentType = features.find((c) => c.id === selectedFeatures?.id);
        if (!establishmentType) {
            dispatch(setSelectedEstablishmentTypeInQuestion(null));
            setSelectedFeatures(null);
            dispatch(setQuestionData([]));
        } else {
            setSelectedFeatures(establishmentType);
        }
    }, [features]);

    useEffect(() => {
        if (features) {
            setFeaturesList(features);
        }
    }, [features]);

    useEffect(() => {
        if (!features.find((c) => c.id === selectedFeatures?.id)) {
            dispatch(setSelectedEstablishmentTypeInQuestion(null));
            setSelectedFeatures(null);
            dispatch(setQuestionData([]));
        }
    }, [features]);

    const onCancel = () => {
        setModalType(null);
        form.resetFields();
        pricingPlanForm.resetFields();
        setEditingRecord(null);
    };

    const handleSave = async (values: any) => {
        try {
            const fixedValues = {
                ...values,
                titleTranslations: sanitizeTranslations(values.titleTranslations),
                descriptionTranslations: sanitizeTranslations(values.descriptionTranslations),
            };
            if (pricingPlanForm.getFieldValue("id")) {
                await dispatch(updatePricingPlan({...editingRecord, ...fixedValues})).unwrap();
            } else {
                await dispatch(addPricingPlan({...fixedValues}));
            }
        } catch (e) {
            console.error(e);
        } finally {
            onCancel();
        }
    };

    const handleSaveEstablishmentType = async (values: any) => {
        const id = await form.getFieldValue("id");
        try {
            const fixedValues = {
                ...values,
                nameTranslations: sanitizeTranslations(values.nameTranslations),
                descriptionTranslations: sanitizeTranslations(values.descriptionTranslations),
            };
            if (id) {
                await dispatch(updateFeatures({ ...editingRecord, ...fixedValues })).unwrap();
            } else {
                await dispatch(addFeatures({...fixedValues}));
            }
            form.resetFields();
            setEditingRecord(null);
        } catch (e) {
            console.error(e);
        } finally {
            onCancel();
        }
    };

    const handleCategoryItemClick = () => {
        setIsEstablishmentTypeOpen(true);
    };

    const handleQuestionsItemClick = (e: any) => {
        setIsEstablishmentTypeQuestionOpen(true);
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            if (showDeleteConfirmItem?.item?.id) {
                if (showDeleteConfirmItem.type === "features") {
                    await dispatch(deleteFeatures({ id: showDeleteConfirmItem?.item?.id })).unwrap();
                } else if (showDeleteConfirmItem.type === "pricingPlan") {
                    await dispatch(deletePricingPlan({id: showDeleteConfirmItem?.item?.id})).unwrap();
                }
            }
        } catch (error) {
            console.error("delete error: ", error);
        } finally {
            setShowDeleteConfirmItem(null);
            setIsDeleting(false);
        }
    };

    const hasPermission = useCallback(
        (permissionKey: string) => checkHasPermission(userRole, permissionKey),
        [userRole]
    );

    const [statusLoading, setStatusLoading] = useState(false);
    const [featureStatusLoading, setFeatureStatusLoading] = useState(false);

    const handlePlanStatusChange = async (item: any) => {
        setStatusLoading(true);
        try {
            await dispatch(updatePricingPlan({
                ...item,
                amount: Number(item.amount),
                days: Number(item.days),
                features: item.features?.map((f: any) => f.id || f),
                isActive: !item.isActive,
            })).unwrap();
        } catch (error: any) {
            console.error("Failed to toggle plan status:", error);
        } finally {
            setStatusLoading(false);
        }
    };

    const handleFeatureStatusChange = async (item: any) => {
        setFeatureStatusLoading(true);
        try {
            await dispatch(updateFeatures({
                ...item,
                isActive: !item.isActive,
            })).unwrap();
        } catch (error: any) {
            console.error("Failed to toggle feature status:", error);
        } finally {
            setFeatureStatusLoading(false);
        }
    };

    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination) return;
        try {
            const updatedTemplates = Array.from(featuresList);
            const [movedItem] = updatedTemplates.splice(result.source.index, 1);
            updatedTemplates.splice(result.destination.index, 0, movedItem);
            setFeaturesList(updatedTemplates);
            const reorderedTemplates = updatedTemplates.map((template, index) => ({
                id: template?.id ?? "",
                index,
            }));
            await dispatch(updateFeaturesOrder({ orderUpdates: reorderedTemplates })).unwrap();
            dispatch(fetchFeatures());
        } catch (error) {
            console.error("Failed to update email template order:", error);
        }
    };

    return (
        <StyledLayout>
            <WebView>
                <StyledContent>
                    {/* are you Column */}
                    <ItemColumn
                        title="Features"
                        buttonLabel="+ Add"
                        hasAddPermission={hasPermission(permissions.AddEditFeatures)}
                        hasEditPermission={hasPermission(permissions.AddEditFeatures)}
                        hasDeletePermission={hasPermission(permissions.DeleteFeatures)}
                        hasViewPermission={hasPermission(permissions.ViewFeatures)}
                        onAddClick={() => {
                            setModalType("features");
                        }}
                        loading={featuresStatus === "loading"}
                        dataSource={features}
                        isSelected={selectedFeatures?.id}
                        onEdit={(item: any) => {
                            setEditingRecord(item);
                            form.setFieldsValue(item);
                            setModalType("features");
                        }}
                        onDelete={(item: any) => setShowDeleteConfirmItem({ item, type: "features" })}
                        isShowDelete={true}
                        handleDragEnd={handleDragEnd}
                        modalType={true}
                        isStatus={true}
                        onStatusChange={handleFeatureStatusChange}
                        statusChecked={(item: any) => item.isActive !== false}
                        statusLoading={featureStatusLoading}
                    />

                    {/* Questions Column */}
                    <ItemColumn
                        title="Pricing Plan"
                        buttonLabel="+ Add"
                        hasAddPermission={hasPermission(permissions.AddEditPricingPlan)}
                        hasEditPermission={hasPermission(permissions.AddEditPricingPlan)}
                        hasDeletePermission={hasPermission(permissions.DeletePricingPlan)}
                        hasViewPermission={hasPermission(permissions.ViewPricingPlan)}
                        onAddClick={() => {
                            setModalType("pricingPlan");
                            pricingPlanForm.setFieldsValue({
                                establishmentType: selectedFeatures?.id,
                            });
                        }}
                        dataSource={pricingPlan}
                        loading={pricingPlanStatus === "loading"}
                        onEdit={(item: any) => {
                            setEditingRecord(item);
                            setModalType("pricingPlan");
                            pricingPlanForm.setFieldsValue({
                                ...item,
                                establishmentType: item?.establishmentType?.id || selectedFeatures?.id,
                            });
                        }}
                        onDelete={(item: any) => setShowDeleteConfirmItem({ item, type: "pricingPlan" })}
                        isStatus={true}
                        onStatusChange={handlePlanStatusChange}
                        statusChecked={(item: any) => item.isActive !== false}
                        statusLoading={statusLoading}
                    />
                </StyledContent>
            </WebView>

            <MobileView>
                <CollapseSection
                    itemKey="1"
                    label={
                        <ItemLabel
                            title="Features"
                            buttonLabel="+ Add"
                            hasAddPermission={hasPermission(permissions.AddEditFeatures)}
                            onButtonClick={() => {
                                setModalType("features");
                            }}
                        />
                    }
                    children={
                        <ItemList
                            hasAddPermission={hasPermission(permissions.AddEditFeatures)}
                            hasEditPermission={hasPermission(permissions.AddEditFeatures)}
                            loading={featuresStatus === "loading"}
                            dataSource={features}
                            onEdit={(item: any) => {
                                setEditingRecord(item);
                                form.setFieldsValue(item);
                                setModalType("features");
                            }}
                            name={'Features'}
                            onDelete={(item: any) => setShowDeleteConfirmItem({ item, type: "features" })}
                            onItemClick={handleCategoryItemClick}
                            isShowDelete={false}
                            isStatus={true}
                            onStatusChange={handleFeatureStatusChange}
                            statusChecked={(item: any) => item.isActive !== false}
                            statusLoading={featureStatusLoading}
                        />
                    }
                />

                <CollapseSection
                    itemKey="2"
                    activeKey={isEstablishmentTypeQuestionsOpen ? ["2"] : []}
                    onClick={(e: any) => {
                        e.stopPropagation(); // Prevents the click from affecting the collapse behavior
                        setIsEstablishmentTypeQuestionOpen(!isEstablishmentTypeQuestionsOpen);
                    }}
                    label={
                        <ItemLabel
                            title="Pricing Plan"
                            buttonLabel="+ Add"
                            hasAddPermission={hasPermission(permissions.AddEditPricingPlan)}
                            onButtonClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                setModalType("pricingPlan");
                                pricingPlanForm.setFieldsValue({
                                    establishmentType: selectedFeatures?.id,
                                });
                            }}
                            onTitleClick={() => setIsEstablishmentTypeQuestionOpen(!isEstablishmentTypeQuestionsOpen)}
                        />
                    }
                    children={
                        <ItemList
                            dataSource={pricingPlan}
                            hasAddPermission={hasPermission(permissions.AddEditPricingPlan)}
                            hasEditPermission={hasPermission(permissions.AddEditPricingPlan)}
                            hasDeletePermission={hasPermission(permissions.DeletePricingPlan)}
                            hasViewPermission={hasPermission(permissions.ViewPricingPlan)}
                            loading={pricingPlanStatus === "loading"}
                            onEdit={(item: any) => {
                                setEditingRecord(item);
                                setModalType("pricingPlan");
                                pricingPlanForm.setFieldsValue({
                                    ...item,
                                    establishmentType: item?.establishmentType?.id || selectedFeatures?.id,
                                });
                            }}
                            onDelete={(item: any) => setShowDeleteConfirmItem({ item, type: "pricingPlan" })}
                            onItemClick={handleQuestionsItemClick}
                            isStatus={true}
                            onStatusChange={handlePlanStatusChange}
                            statusChecked={(item: any) => item.isActive !== false}
                            statusLoading={statusLoading}
                        />
                    }
                />
            </MobileView>

            <CreateEditCategoryModal editingRecord={editingRecord} setEditingRecord={setEditingRecord} onCancel={onCancel} form={form} handleSave={handleSaveEstablishmentType} isModalOpen={modalType === "features"} hasAddPermission={hasPermission(permissions.AddEditFeatures)} hasEditPermission={hasPermission(permissions.AddEditFeatures)} hasViewPermission={hasPermission(permissions.ViewFeatures)} />
            <CreateEditQuestionModal onCancel={onCancel} form={pricingPlanForm} handleSave={handleSave} isModalOpen={modalType === "pricingPlan"} hasAddPermission={hasPermission(permissions.AddEditPricingPlan)} hasEditPermission={hasPermission(permissions.AddEditPricingPlan)} hasViewPermission={checkHasPermission(userRole, permissions.ViewPricingPlan)} />

            {showDeleteConfirmItem?.item?.id && (
                <ConfirmationModal
                    handleSave={() => handleDelete()}
                    isModalOpen={!!showDeleteConfirmItem?.item?.id}
                    onCancel={() => setShowDeleteConfirmItem(null)}
                    title={`Delete ${showDeleteConfirmItem.type}`}
                    message={`Are you sure you want to delete the ${showDeleteConfirmItem.type} ${["establishmentType"].includes(showDeleteConfirmItem.type) ? (showDeleteConfirmItem?.item as Features)?.name : (showDeleteConfirmItem?.item as PricingPlan)?.title
                    }?`}
                    confirmButtonText="Delete"
                    loading={isDeleting}
                />
            )}
        </StyledLayout>
    )
};

export default EstablishmentTypeAreYou;
